/**
 * Derives `/team_role/{id}` role tokens from a Firestore user document and (optionally) JWT claims.
 * Used by AuthProvider for expandTeamRoles + canonical `team_roles` writes.
 *
 * Custom claims often carry NN/ETHIC in `team_roles` before the user doc is fully synced; union both sources.
 */
import { normalizeTeamRoleId } from '../../lib/idNormalization';

const normalizeString = (value?: unknown) => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

const extractDocId = (ref: unknown): string | undefined => {
  if (!ref) return undefined;

  if (typeof ref === 'object' && ref !== null) {
    const maybe = ref as { id?: unknown; path?: unknown };
    const id = normalizeString(maybe.id);
    if (id) return id;

    if (typeof maybe.path === 'string') {
      const fromPath = maybe.path.split('/').pop();
      return normalizeString(fromPath);
    }
  }

  if (typeof ref === 'string') {
    const fromPath = ref.split('/').pop();
    return normalizeString(fromPath);
  }

  return undefined;
};

/**
 * Collects team role ids from teamIds refs plus a **union** of `team_roles` and `teamRoles`.
 * Using only `team_roles ?? teamRoles` drops roles when both fields exist (e.g. NN strings + ETHIC objects).
 * When `claims` is passed, the same shapes are read from the ID token (parity with Firestore rules `request.auth.token`).
 */
export function extractTeamRoleIdsFromUserDocument(
  userData: Record<string, unknown> | null | undefined,
  claims?: Record<string, unknown> | null | undefined,
): string[] {
  const deduped: string[] = [];
  const seen = new Set<string>();

  const pushTeamRoleEntry = (entry: unknown) => {
    let raw: string | undefined;
    if (typeof entry === 'string') {
      raw = entry.trim() || undefined;
    } else if (entry && typeof entry === 'object') {
      const obj = entry as { role?: string; teamCode?: string; teamId?: string; id?: string };
      raw = (obj.role ?? obj.teamCode ?? obj.teamId ?? extractDocId(entry))?.trim() || undefined;
    } else {
      raw = extractDocId(entry);
    }
    const normalized = raw ? normalizeTeamRoleId(raw) : undefined;
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    deduped.push(normalized);
  };

  const addFromTeamIds = (rawTeamIds: unknown) => {
    const teamIds = Array.isArray(rawTeamIds)
      ? rawTeamIds
      : rawTeamIds != null
        ? [rawTeamIds]
        : [];
    teamIds.forEach((entry) => {
      const raw = extractDocId(entry);
      const normalized = raw ? normalizeTeamRoleId(raw) : undefined;
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      deduped.push(normalized);
    });
  };

  const ingest = (record: Record<string, unknown> | null | undefined) => {
    if (!record) return;
    addFromTeamIds((record as { teamIds?: unknown }).teamIds);

    const teamRolesFromField = (record as { team_roles?: unknown }).team_roles;
    const teamRolesLegacy = (record as { teamRoles?: unknown }).teamRoles;
    const arrFromTeamRoles = Array.isArray(teamRolesFromField) ? teamRolesFromField : [];
    const arrFromTeamRolesLegacy = Array.isArray(teamRolesLegacy) ? teamRolesLegacy : [];
    arrFromTeamRoles.forEach(pushTeamRoleEntry);
    arrFromTeamRolesLegacy.forEach(pushTeamRoleEntry);
  };

  ingest(userData ?? undefined);
  ingest(claims ?? undefined);

  return deduped;
}
